import {
  getPersistentString,
  setPersistentString,
} from '@ps/functions/utils/persistent-string-storage';

/**
 * Per-user cache of which posts the viewer has liked, so the `/liked` API is hit
 * at most ONCE per post (then served from here). Backed by the persistent
 * string store (IndexedDB, localStorage fallback). Keyed by uid so switching
 * accounts can't leak state; cross-DEVICE staleness is accepted (a like on
 * another device won't reflect on one that cached the old value — local toggles
 * keep this device consistent).
 *
 * Stored compactly as one JSON map `{ [postId]: boolean }` under `posts-liked:<uid>`.
 */
type LikedMap = Record<string, boolean>;

function storageKey(uid: string): string {
  return `posts-liked:${uid}`;
}

async function readMap(uid: string): Promise<LikedMap> {
  const raw = await getPersistentString(storageKey(uid));
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed != null && typeof parsed === 'object'
      ? (parsed as LikedMap)
      : {};
  } catch {
    return {};
  }
}

/** The cached liked-state for a post, or `undefined` when never checked. */
export async function getCachedLiked(
  uid: string,
  postId: string,
): Promise<boolean | undefined> {
  const map = await readMap(uid);
  const value: boolean | undefined = map[postId];
  return value;
}

/** Record the viewer's liked-state for a post (after a check or a toggle). */
export async function setCachedLiked(
  uid: string,
  postId: string,
  liked: boolean,
): Promise<void> {
  const map = await readMap(uid);
  map[postId] = liked;
  await setPersistentString(storageKey(uid), JSON.stringify(map));
}
